// eslint-disable-next-line no-unused-vars
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualTooltip } from 'components/virtualTooltip/useVirtualTooltip';
import { WorkspaceContext } from 'views/workspace/workspaceContext';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';

const { updateSelection } = WorkspaceActionCreator;

interface IArgs {
	svgRef: React.MutableRefObject<SVGElement>;
	containerRef: React.MutableRefObject<SVGGElement>;
	data: any;
	dimensions: { height: number; width: number };
	options: any;
	Chart;
	renderer;
	supportSelection?: boolean;
}

export interface IReturnVal {
	TooltipElement: JSX.Element;
	chartMethods: ChartReturn;
}

interface IUpdateArgs {
	data: any;
	dimensions: { height: number; width: number };
	options: any;
	selection: any;
}

interface ChartReturn {
	update: (args: IUpdateArgs) => void;
	clear: () => void;
}

/**
 * @param svgRef
 * @param containerRef
 * @param data
 * @param dimensions
 * @param options
 * @param Chart
 * @param renderer
 * @param supportSelection
 */
export function useChart({
	svgRef,
	containerRef,
	data,
	dimensions,
	options,
	Chart,
	renderer,
	supportSelection = false,
}: IArgs): IReturnVal {
	// context can be null in a dashboard, so disable selection
	const context = useContext(WorkspaceContext);
	let selection = context?.state.transient.selection;
	let dispatch = context?.dispatch;

	// store the chart for use between runs
	const [localChart, setLocalChart] = useState<ChartReturn>();

	// keep track of selection locally so we use a consistent object for d3's sake
	const selectionRef = useRef([]);

	// manage the selection
	const localSelection = useMemo(() => {
		// update selection
		if (supportSelection) {
			if (selection === null) {
				selectionRef.current.length = 0;
			} else {
				selectionRef.current.push(selection);
			}
		}

		return selectionRef.current;
	}, [selection, supportSelection]);

	// create the tooltip element and the function to update the tooltip
	const { TooltipElement, updateTooltip } = useVirtualTooltip(renderer);

	useEffect(() => {
		if (data && dimensions) {
			let ch = localChart;

			// if we don't have an existing chart, create one
			if (!ch) {
				ch = Chart({
					svg: svgRef.current,
					container: containerRef?.current,
					updateTooltip,
					updateSelection: dispatch ? (selection) => dispatch(updateSelection(selection)) : () => {},
				});
				setLocalChart(ch);
			}

			// @ts-ignore
			ch.update({ data, dimensions, options, selection: localSelection });
		}
	}, [
		Chart,
		data,
		dimensions,
		localChart,
		options,
		updateTooltip,
		svgRef,
		containerRef,
		renderer,
		selection,
		dispatch,
		localSelection,
		supportSelection,
	]);

	return { TooltipElement, chartMethods: localChart };
}
